/**
 * Leaderboard ranking — a pure function of players, runs, query and caller.
 * OfflineDemoBackend uses it directly; the server implements the same rules in
 * SQL. The rules are specified on `LeaderboardSearch` in api-types.ts.
 */
import type {
  LeaderboardEntry,
  LeaderboardPage,
  LeaderboardQuery,
  LeaderboardScope,
  PlayerProfile,
} from '../../contracts.ts';
import { findSaProvince } from '../geo.ts';
import { foldText } from '../text.ts';
import { clampLeaderboardLimit, LEADERBOARD_SCOPES, LEADERBOARD_WEEK_MS } from './api-types.ts';

/** The player fields a board needs (PlayerProfile satisfies it). */
export type BoardPlayer = Pick<
  PlayerProfile,
  | 'playerId'
  | 'displayName'
  | 'grade'
  | 'schoolId'
  | 'schoolName'
  | 'city'
  | 'province'
  | 'country'
  | 'onboarded'
  | 'leaderboardOptIn'
>;

/** A stored, accepted run. Flagged runs are kept but never ranked. */
export interface BoardRun {
  runId: string;
  playerId: string;
  raw: number;
  rated: number;
  levelReached: number;
  /** Epoch ms when the run was submitted. */
  at: number;
  flagged: boolean;
  /**
   * The player's first completed run: it sets their level but is never ranked
   * (GAME_DESIGN § 5). Absent on runs stored before placement existed = ranked.
   */
  placement?: boolean;
  /** A "More time" (relaxed pace) run: stored and rated, never ranked (GAME_DESIGN § 4.1). */
  relaxed?: boolean;
}

export interface BoardInput {
  players: readonly BoardPlayer[];
  runs: readonly BoardRun[];
  query: LeaderboardQuery;
  /** The caller (for scope defaults and the `me` row); null when anonymous/unknown. */
  me: BoardPlayer | null;
  /** Clock for the "week" period. */
  now: number;
  source: LeaderboardPage['source'];
}

/** Build one leaderboard page. Never throws; an unresolvable scope gives an empty page. */
export function buildLeaderboard(input: BoardInput): LeaderboardPage {
  const ranked = rankPlayers(input);
  const limit = clampLeaderboardLimit(input.query.limit);
  const entries = ranked.slice(0, limit);
  return {
    entries,
    me: ranked.find((e) => e.isMe === true) ?? null,
    total: ranked.length,
    source: input.source,
  };
}

/**
 * The caller's rank on the rated, all-time board of every scope they appear
 * in (SubmitResult.ranks).
 */
export function ranksFor(input: Omit<BoardInput, 'query' | 'source'>): Partial<Record<LeaderboardScope, number>> {
  const ranks: Partial<Record<LeaderboardScope, number>> = {};
  for (const scope of LEADERBOARD_SCOPES) {
    const { me } = buildLeaderboard({ ...input, source: 'offline-demo', query: { metric: 'rated', scope, period: 'all', limit: 1 } });
    if (me) ranks[scope] = me.rank;
  }
  return ranks;
}

/** All ranked rows for the query, best first, with competition ranks and isMe marks. */
function rankPlayers({ players, runs, query, me, now }: BoardInput): LeaderboardEntry[] {
  const inScope = scopeFilter(query, me);
  if (!inScope) return [];
  const grade = query.grade ?? null;
  const eligible = new Map<string, BoardPlayer>();
  for (const p of players) {
    if (!p.onboarded || !p.leaderboardOptIn || p.grade === null) continue;
    if (grade !== null && p.grade !== grade) continue;
    if (inScope(p)) eligible.set(p.playerId, p);
  }

  const since = query.period === 'week' ? now - LEADERBOARD_WEEK_MS : -Infinity;
  const score = (r: BoardRun) => (query.metric === 'raw' ? r.raw : r.rated);
  const best = new Map<string, { run: BoardRun; player: BoardPlayer }>();
  for (const run of runs) {
    const player = eligible.get(run.playerId);
    if (!player || run.flagged || run.placement === true || run.relaxed === true || run.at < since) continue;
    const current = best.get(run.playerId)?.run;
    if (!current || score(run) > score(current) || (score(run) === score(current) && run.at < current.at)) {
      best.set(run.playerId, { run, player });
    }
  }

  const rows = [...best.values()].sort(
    (a, b) =>
      score(b.run) - score(a.run) ||
      a.run.at - b.run.at ||
      (a.player.playerId < b.player.playerId ? -1 : a.player.playerId > b.player.playerId ? 1 : 0),
  );
  let rank = 0;
  return rows.map(({ run, player }, i) => {
    const prev = rows[i - 1];
    if (!prev || score(prev.run) !== score(run)) rank = i + 1;
    const entry: LeaderboardEntry = {
      rank,
      entryKey: boardEntryKey(player.playerId),
      displayName: player.displayName,
      grade: player.grade ?? 0,
      schoolName: player.schoolName,
      city: player.city,
      province: player.province,
      country: player.country,
      score: score(run),
      levelReached: run.levelReached,
      at: boardDay(run.at),
    };
    if (me !== null && player.playerId === me.playerId) entry.isMe = true;
    return entry;
  });
}

/**
 * Opaque row key for a public board entry (LeaderboardEntry.entryKey): a
 * stable 53-bit hash of the player id, so a board never shows the id itself.
 * The server keys rows with an HMAC; the offline demo only needs a key that is
 * stable and collision-free in practice, since its data never leaves the device.
 */
export function boardEntryKey(playerId: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < playerId.length; i++) {
    const c = playerId.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `e${(4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36)}`;
}

/** The public, coarse form of a run time: its UTC day, YYYY-MM-DD (LeaderboardEntry.at). */
export function boardDay(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10);
}

type PlayerFilter = (p: BoardPlayer) => boolean;

/**
 * Location/grade filter for a scope; null when the value it needs is unknown.
 * The location comes from the query when it names any of country/province/city
 * (browsing another place), otherwise from the caller's profile — never a mix,
 * so asking for city "Durban" isn't silently narrowed to the caller's province.
 */
function scopeFilter(query: LeaderboardQuery, me: BoardPlayer | null): PlayerFilter | null {
  const from = query.country || query.province || query.city ? query : me;
  const country = countryKey(from?.country ?? '');
  const province = provinceKey(from?.province ?? '');
  const city = foldText(from?.city ?? '');
  const sameCountry: PlayerFilter = (p) => !country || countryKey(p.country) === country;
  const sameProvince: PlayerFilter = (p) => !province || provinceKey(p.province) === province;

  switch (query.scope) {
    case 'global':
      return () => true;
    case 'country':
      return country ? sameCountry : null;
    case 'province':
      return province ? (p) => sameProvince(p) && sameCountry(p) : null;
    case 'city':
      return city ? (p) => foldText(p.city) === city && sameProvince(p) && sameCountry(p) : null;
    case 'school': {
      const schoolId = query.schoolId || me?.schoolId;
      return schoolId ? (p) => p.schoolId === schoolId : null;
    }
    case 'grade': {
      const grade = query.grade ?? me?.grade ?? null;
      return grade !== null ? (p) => p.grade === grade : null;
    }
  }
}

function countryKey(country: string): string {
  return country.trim().toUpperCase();
}

/** SA provinces compare by code (names in either language resolve to it); anything else by folded text. */
function provinceKey(province: string): string {
  return findSaProvince(province)?.code ?? foldText(province);
}
